import React, { useState } from "react";
import ContentWrapper from "../../components/layout/ContentWrapper";
import { useGetOrdersQuery } from "./queries";
import { OrderStateArray, OrderTableQuery } from "./models";
import OrdersTable from "./components/OrdersTable";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import {
  Filter,
  FlexContent,
  PageHeading,
  SearchBox,
  useAuth,
} from "../../components";
import { SelectItem } from "../../components/select/Select";
import {
  getTableFiltersFromCodebooks,
  getTableParsedFiltersForQuery,
} from "../../helpers/tableHelpers";
import { useGetCodebooksQuery } from "../../common/queries";
import { useCustomers } from "../../helpers/customersHelper";

const defaultTableQuery: OrderTableQuery = {
  page: 1,
  pageSize: 10,
};

const OrdersPage: React.FC = (): React.ReactElement => {
  const { tOrder, tCommon, t } = useCustomTranslation();
  const codebooksQuery = useGetCodebooksQuery();
  const { user } = useAuth();
  const { loadCustomersForFilter } = useCustomers();

  const [queryParams, setQueryParams] =
    useState<OrderTableQuery>(defaultTableQuery);

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, SelectItem[]>
  >({});

  const query = useGetOrdersQuery(queryParams);

  const handleSearch = (searchPhrase: string) => {
    setQueryParams({ ...queryParams, searchPhrase });
  };

  const handleFilter = (selectedFilters: Record<string, SelectItem[]>) => {
    const parsedFilters = getTableParsedFiltersForQuery(selectedFilters);

    setQueryParams({
      searchPhrase: queryParams.searchPhrase,
      ...defaultTableQuery,
      ...parsedFilters,
    });
    setSelectedFilters(selectedFilters);
  };

  return (
    <ContentWrapper htmlTitle={tCommon("menu_orders")}>
      <FlexContent className="mb-4">
        <PageHeading title={tOrder("table_heading")} />
        <FlexContent className="gap-4">
          <Filter
            selectedFitlers={selectedFilters}
            updateFilters={handleFilter}
            filters={[
              ...getTableFiltersFromCodebooks(
                [{ key: "categoryId", source: "orderCategories" }],
                t,
                codebooksQuery.data,
              ),
              {
                key: "orderState",
                label: tOrder("state"),
                options: OrderStateArray.map((state) => ({
                  value: state,
                  label: tOrder(`state_${state}`),
                })),
              },
              {
                key: "customerId",
                label: tOrder("customer"),
                isAsync: true,
                options: [],
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                loadOptions: (inputValue, cb) =>
                  loadCustomersForFilter(inputValue, cb, "isEndCustomer"),
                hidden: !user?.isInvoiceCustomer,
              },
              {
                key: "invoiceCustomerId",
                label: tOrder("invoice_customer"),
                isAsync: true,
                options: [],
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                loadOptions: (inputValue, cb) =>
                  loadCustomersForFilter(inputValue, cb, "isInvoiceCustomer"),
                hidden: !user?.hasOrdersWithInvoiceCustomer,
              },
            ]}
          />
          <SearchBox onSearch={handleSearch} />
        </FlexContent>
      </FlexContent>
      <OrdersTable
        data={query.data}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        loading={query.isLoading}
      />
    </ContentWrapper>
  );
};

export default OrdersPage;
